import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { tasks as initialTasks } from "@/data/dummy";

export interface Task {
  id: string;
  /** Owner user id — only present on production (backend) tasks; preview/dummy tasks scope by assignee name instead. */
  userId?: string | number;
  title: string;
  priority: string;
  dueDate: string;
  completed: boolean;
  assignee: string;
  notes?: string;
  linkedPeople?: string[];
  linkedCompanies?: string[];
  linkedDeals?: string[];
  /**
   * Set when the task was assigned to the current owner by another teammate
   * (i.e. it did not originate from the owner themselves, an AI agent, or a
   * meeting extraction). Used to surface the "ribbon" origin cue in the tasks
   * list and the "whisper" attribution in the detail sheet.
   */
  assignedBy?: string;
  /** ISO timestamp / display string for when the assignment happened. */
  assignedAt?: string;
  /**
   * How the task originally came to exist. Used to render the "whisper"
   * origin icon at the start of each row (and in the detail sheet):
   *  - `assigned` (implied when `assignedBy` is set) — teammate assigned it
   *  - `ai` — suggested by Coffee AI
   *  - `manual` — created by the owner themselves (default when omitted)
   */
  origin?: "ai" | "manual";
  /**
   * When `origin === "ai"`, optional attribution to the meeting Coffee AI
   * pulled the suggestion from. Rendered in the tooltip and detail sheet as
   * e.g. "Suggested by AI from 2/27 meeting with Sarah Chen".
   */
  aiSource?: { meetingDate: string; personName: string };
}

interface TasksContextType {
  tasks: Task[];
  addTasks: (newTasks: Omit<Task, "id" | "completed">[]) => void;
  upsertTask: (task: Task) => void;
  toggleComplete: (id: string) => void;
  updateTask: (id: string, updates: Partial<Omit<Task, "id">>) => void;
}

const TasksContext = createContext<TasksContextType | null>(null);

export function TasksProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>(initialTasks);

  const addTasks = useCallback((newTasks: Omit<Task, "id" | "completed">[]) => {
    setTasks(prev => {
      const nextId = Math.max(...prev.map(t => parseInt(t.id)), 0) + 1;
      const today = new Date();
      const fallbackDate = `${today.toLocaleString("en-US", { month: "short" })} ${today.getDate()}, ${today.getFullYear()}`;
      const additions: Task[] = newTasks.map((t, i) => ({
        ...t,
        id: String(nextId + i),
        completed: false,
        dueDate: t.dueDate || fallbackDate,
      }));
      return [...prev, ...additions];
    });
  }, []);

  const upsertTask = useCallback((task: Task) => {
    setTasks(prev => {
      if (prev.some(t => t.id === task.id)) return prev;
      return [...prev, task];
    });
  }, []);

  const toggleComplete = useCallback((id: string) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  }, []);

  const updateTask = useCallback((id: string, updates: Partial<Omit<Task, "id">>) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, []);

  return (
    <TasksContext.Provider value={{ tasks, addTasks, upsertTask, toggleComplete, updateTask }}>
      {children}
    </TasksContext.Provider>
  );
}

export function useTasks() {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error("useTasks must be used within TasksProvider");
  return ctx;
}
